/* tslint:disable:max-line-length */
import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, NavigationExtras, Route, Router, RouterStateSnapshot, Routes, UrlTree} from '@angular/router';
import {ConfigPermission} from '@docma-mm-core/permission/config-permission';
import {Project as MmProject} from '@docma-mm-core/project/project';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {distinctUntilChanged, filter, first, map, switchMap} from 'rxjs/operators';
import {MmProjectPermissionsService} from '../mm-project/mm-project-permissions-service';
import {MmProjectService} from '../mm-project/mm-project.service';
import {MmRoutesEnum} from './mm-routes.enum';
import {routeToPath} from './mm-routes.map';
import {Customer, CustomerService} from '@mydocma/customer';
import {MmRoutingDataService} from './mm-routing-data.service';

@UntilDestroy()
@Injectable()
export class MmRoutingService  implements OnDestroy {

  private routesMap = new Map<string, Route>();
  private moduleActivatedRoute: ActivatedRoute;
  private moduleActivatedRouteSubject = new BehaviorSubject<ActivatedRoute>(null);
  public moduleActivatedRoute$ = this.moduleActivatedRouteSubject.asObservable();
  public isSuspended = false;


  projectChangedInternal = new BehaviorSubject<MmProject>(undefined);
  customerChangedInternal = new BehaviorSubject<Customer>(undefined);

  constructor(private readonly projectService: MmProjectService,
              private readonly projectPermissionsService: MmProjectPermissionsService,
              private readonly customerService: CustomerService,
              private readonly router: Router,
              private readonly activatedRoute: ActivatedRoute,
              private readonly zone: NgZone,
              private readonly routingDataService: MmRoutingDataService) {

    this.projectChangedInternal
      .asObservable()
      .pipe(untilDestroyed(this))
      .subscribe(project => {
        this.onProjectChanged(project);
      });

    this.customerChangedInternal
      .asObservable()
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(customer => {
        this.onCustomerChanged(customer);
      });

    this.customerService.onCurrentCustomerChanged
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(customer => {
        if (this.router.url.match(/^\/customers\/[0-9a-fA-F-]{36}\/projects-overview/)) {
          const match = this.router.url.match(/[0-9a-fA-F-]{36}/);
          const customerId = (match ? match[0] : null);
          if (customer) { // && customer.id !== customerId
            this.router.navigate([`/${MmRoutesEnum.customers}/${customer.id}/projects-overview`]);
          }
        }
      });

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(e => e instanceof NavigationEnd),
        switchMap(e => this.projectService.currentProject
          .pipe(switchMap(project => this.customerService.currentCustomer
            .pipe(map(customer => ({project, customer}))) )))
      )
      .subscribe(p => {
        if (this.isSuspended) {
          console.warn('### routingService.suspended ###');
          return;
        }

        this.moduleActivatedRoute = this.getModuleRoute(this.activatedRoute) as ActivatedRoute;
        if (this.moduleActivatedRoute) {
          const regex = new RegExp(`^\/${MmRoutesEnum.customers}`, 'i');

          if (!regex.test(this.router.routerState.snapshot.url)) {
            this.routesMap.clear();
            this.addRoutesToMap([this.moduleActivatedRoute.routeConfig]);
            this.projectChangedInternal.next(p.project);
          } else if (regex.test(this.router.routerState.snapshot.url)) {
            this.routesMap.clear();
            this.addRoutesToMap([this.moduleActivatedRoute.routeConfig]);
            this.customerChangedInternal.next(p.customer);
          }
          this.moduleActivatedRouteSubject.next(this.moduleActivatedRoute);
        }
      });

    combineLatest(
      [
        this.activatedRoute.data,
        this.projectPermissionsService.permissions$]
    )
      .pipe(
        untilDestroyed(this),
        switchMap(([data, project]) => this.projectPermissionsService.hasProjectConfigPermissions( data && data.configPermissions).pipe(first()))
      )
      .subscribe(hasPermissions => {
        if (!hasPermissions) {
            this.navigate(['access_denied'], { queryParams: { url: this.activatedRoute.snapshot.url } });
        }
      });

  }

  ngOnDestroy(): void {
  }

  private addRoutesToMap(routes: Routes, parentPath: string= '') {
    if (!routes || routes.length === 0) {
      return ;
    }
    routes.forEach(route => {
        const path =   (parentPath + '/' + route.path).replace('//', '/').toLowerCase();
        this.routesMap.set(path, route);
        this.addRoutesToMap(route.children, path);
      });
  }

  public getModuleRoute(route: ActivatedRouteSnapshot | ActivatedRoute): ActivatedRouteSnapshot | ActivatedRoute {
    const snapshot = route instanceof ActivatedRoute ? route.snapshot :  route;
    if (snapshot.component && (snapshot.component as any).name === 'MmMainComponent') {
      return route;
    }
    for (const childRoute of route.children) {
      const mr = this.getModuleRoute(childRoute);
      if (mr) {
        return mr;
      }
    }
    return null;
  }

  public getRoutePath(route: ActivatedRouteSnapshot): string {
    let path = route.routeConfig.path;
    if (route.children && route.children.length === 1) {
      const cp = this.getRoutePath(route.children[0]);
      if (cp) {
        path = path + '/' + cp;
      }
    }
    return path;
  }

  private onCustomerChanged(c: Customer) {
    if (this.routingDataService.currentCustomer && c?.id !== this.routingDataService.currentCustomer?.id) {
      console.warn('### currentCustomer cleared ###');
      delete this.routingDataService.currentCustomer;
    }
    if (this.moduleActivatedRoute) {
      const path = this.getRoutePath(this.moduleActivatedRoute.snapshot);
      if (c) {
        if (path && path.includes(':customerId')) {
          this.navigateToCustomer(path, c, this.moduleActivatedRoute.snapshot.queryParams);
        }
      }
    }
  }

  private onProjectChanged(p: MmProject) {
    if (this.routingDataService.currentProject && p?.MyDocmaUid !== this.routingDataService.currentProject?.id) {
      console.warn('### currentProject cleared ###');
      delete this.routingDataService.currentProject;
    }
    if (this.moduleActivatedRoute) {
      const path = this.getRoutePath(this.moduleActivatedRoute.snapshot);
      if (!p) {
        this.navigateTo('/projects', null, this.moduleActivatedRoute.snapshot.queryParams);
      } else {
        if (path && path.includes(':projectId')) {
          this.navigateTo(path, p, this.moduleActivatedRoute.snapshot.queryParams);
        } else if (!path.endsWith(MmRoutesEnum.projects_overview)) {
          this.navigateTo(routeToPath.get(MmRoutesEnum.faults), p, this.moduleActivatedRoute.snapshot.queryParams);
        }
      }
    }
  }

  private navigateToCustomer(path: string, customer: Customer = null, queryParams?: {[key: string]: any}) {

    let u = path;
    if (customer && customer.id) {
      u = u.replace(':customerId', customer.id);
    }
    this.navigate([u], { relativeTo: this.moduleActivatedRoute, queryParams });
  }

  private navigateTo(path: string, project: MmProject = null, queryParams?: {[key: string]: any}) {

    let u = path;
    if (project && project.MyDocmaUid) {
      u = u.replace(':projectId', project.MyDocmaUid);
    }
    this.navigate([u], { relativeTo: this.moduleActivatedRoute, queryParams });
  }

  public navigate(commands: any[], extras?: NavigationExtras) {
    this.zone.run(() => {
       const params: NavigationExtras = {};
       Object.assign(params, extras || {});
       params.relativeTo = this.moduleActivatedRoute;
       this.router.navigate(commands, params);
    });
  }

  public getRouteByPath(path: string): Route {
    return this.routesMap.get(path.toLowerCase());
  }

  public canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot):
    boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {

    const requiredConfigPermissions: ConfigPermission[] = childRoute.data.configPermissions;

    return this.projectPermissionsService.hasProjectConfigPermissions(requiredConfigPermissions)
      .pipe(
        first(),
        map(p => {
          if (!p) {
            const moduleRoute = this.getModuleRoute(childRoute.root) as ActivatedRouteSnapshot;
            const path = this.getRoutePath(moduleRoute);
            return this.router.createUrlTree(['access_denied'], { queryParams: { url: path } });
          }
          return p ;
        })
      );

  }




}
